import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { TreeNode } from 'primeng/api';
import { v4 as uuidv4 } from 'uuid';

import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { JournodexGeographicEntityModel } from '../../../Common/Journodex/models/journodex-geographic-entity.model';
import { DataSelectDialogServiceInterface, IncludeOrExclude } from '../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { JournodexGeographyModel } from '../../../Common/Journodex/models/journodex-geography.model';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';

/** used for sorting geography types display order */
const geographyTypeRankingMap = new Map<GeographyTypesEnum, number>([
    [ GeographyTypesEnum.city,      4 ],
    [ GeographyTypesEnum.county,    3 ],
    [ GeographyTypesEnum.province,  2 ],
    [ GeographyTypesEnum.country,   1 ],
    [ GeographyTypesEnum.continent, 0 ]
]);

class PartiallySelectedParentsModel {
    partiallySelected: boolean;
    data: JournodexGeographicEntityModel;

    constructor(partiallySelected: boolean, data: JournodexGeographicEntityModel) {
        this.partiallySelected = partiallySelected;
        this.data = data;
    }
}

/**
 * This class is a service that handles fetching data on available regions and handles the logic for selecting
 * regions to be either included or excluded from a search.
 *
 * Three lists of regions need to be managed in this service:
 * 1. listItems -  the available regions to choose from
 * 2. flattenedSelectedRegions - an array listing all of the individual selected regions regardless of their relationship to other regions
 * 3. selectedListItems -  list of selected regions that contians only the highest level geographic regions (lists the parents, but not the children)
 *
 */
export abstract class RegionSelectDialogService implements DataSelectDialogServiceInterface {
    /** a list of selected regions that contians only the highest level geographic regions (lists the parents, but not the children) */
    selectedListItemsToDisplay: TreeNode[] = [];
    /** the available regions to choose from */
    listItems: TreeNode[];
    /** an array listing all of the individual selected regions regardless of their relationship to other regions*/
    flattenedSelectedRegions: TreeNode[] = [];

    protected listItemProvider: BehaviorSubject<TreeNode[]>;
    protected flattenedSelectedListItemsProvider: BehaviorSubject<TreeNode[]>;
    protected selectedItemsToDisplayProvider: BehaviorSubject<TreeNode[]>;

    otherSelectedItems: JournodexGeographyModel[] = [];
    state: IncludeOrExclude;

    regionTypes = [
        GeographyTypesEnum.city,
        GeographyTypesEnum.county,
        GeographyTypesEnum.province,
        GeographyTypesEnum.country,
        GeographyTypesEnum.continent
    ];

    availableSelectedItemTypes: Map<GeographyTypesEnum, string> = new Map(
        [
            [GeographyTypesEnum.continent, 'Monitoring.search.continents'],
            [GeographyTypesEnum.country, 'Monitoring.search.countries'],
            [GeographyTypesEnum.province, 'Monitoring.search.provinces'],
            [GeographyTypesEnum.county, 'Monitoring.search.counties'],
            [GeographyTypesEnum.city, 'Monitoring.search.cities']
        ]
    );
    selectedItemTypes: Map<GeographyTypesEnum, string>;
    protected selectedItemTypesProvider: BehaviorSubject<Map<GeographyTypesEnum, string>>;

    /** a mapping of partially selected parent regions, to keep component in sync with flattenedSelectedRegions list*/
    partiallySelectedParentsMap: Map<GeographyTypesEnum, JournodexGeographicEntityModel[]> = new Map(
        [
            [GeographyTypesEnum.continent, []],
            [GeographyTypesEnum.country, []] ,
            [GeographyTypesEnum.province, []],
            [GeographyTypesEnum.county, []],
        ]
    );

    subscriptions: Subscription[] = [];
    /** stores the journodex geography models of regions that should be on the search query */
    selectedRegions: JournodexGeographyModel[] = [];

    constructor(
        protected geographyService: JournodexRegionsService,
    ) {
        this.listItemProvider = new BehaviorSubject<TreeNode[]>(null);
        this.flattenedSelectedListItemsProvider = new BehaviorSubject<TreeNode[]>(null);
        this.selectedItemTypesProvider = new BehaviorSubject<Map<GeographyTypesEnum, string>>(null);
        this.selectedItemTypesProvider.next(this.selectedItemTypes);
        this.selectedItemsToDisplayProvider = new BehaviorSubject<TreeNode[]>(null);
    }

    abstract applySelectedListItems(selectedItems: TreeNode[]);
    abstract setAppropriateListItems(): void;

    private cloneTree(nodes: TreeNode[], parent?: TreeNode): TreeNode[] {
        return nodes.map(node => {
            const clone = { ...node, parent };
            clone.children = clone.children ? this.cloneTree(clone.children, clone) : clone.children;
            return clone;
        });
    }

    /**
     * Getter for available list items to select from
     *
     * @returns BehaviorSubject<TreeNode[]>
     */
    getListItems(): Observable<TreeNode[]> {
        return this.listItemProvider;
    }

    /**
     * Getter for selectedListItemsProvider (flattened selected list items)
     */
    getSelectedListItems(): Observable <TreeNode[]> {
        return this.flattenedSelectedListItemsProvider;
    }

    /**
     * Getter for the selectedItemTypesProvider
     */
    getSelectedItemTypes(): Observable<Map<GeographyTypesEnum, string>> {
        return this.selectedItemTypesProvider;
    }

    /**
     * Getter for the highest level selected list items (to display as selected)
     */
    getSelectedItemsToDisplay(): Observable<TreeNode[]> {
        return this.selectedItemsToDisplayProvider;
    }

    // required by interface
    itemSelected(item: TreeNode): void {}

    public filterItems(query: string): Observable<TreeNode[]> {
        throw new Error('Filtering not implemented');
    }

    /**
     * Walks the list of items and sets partial selection to false for relevant regions
     *
     * @param regionsToUnselect - object whose properties are journodex models that should be unselected
     *
     * @param items - nodes to iterate over and change partial selection for
     */
    walkItemsAndPartiallyUnselectChildren(
        regionsToUnselect: Map<GeographyTypesEnum, PartiallySelectedParentsModel>,
        items: TreeNode[]
    ): void {
        items.forEach(listItem => {
            const listItemType = listItem.data.geographicType;
            const parentOfDeletedItem = regionsToUnselect.get(listItemType);

            if (
                parentOfDeletedItem
                && parentOfDeletedItem.data.id === listItem.data.id
            ) {
                listItem.partialSelected = parentOfDeletedItem.partiallySelected;
                regionsToUnselect.delete(listItemType);

                if (listItem.children && regionsToUnselect.size) {
                    this.walkItemsAndPartiallyUnselectChildren(regionsToUnselect, listItem.children);
                }
            }
        });
    }

    /**
     * Sets selectedItemTypes, and calls next on provider
     */
    setSelectedItemTypes(): void {
        const selectedItemTypesMap = new Map<GeographyTypesEnum, string>();
        this.selectedListItemsToDisplay.forEach(item => {
            const itemType = (item.type as GeographyTypesEnum);

            const valueFromSelectedTypeMap = selectedItemTypesMap.get(itemType);

            if (valueFromSelectedTypeMap) {
                return;
            }

            selectedItemTypesMap.set(
                itemType,
                this.availableSelectedItemTypes.get(itemType)
            );
        });

        // assign item types in correct order
        this.selectedItemTypes = new Map(
            [...selectedItemTypesMap.entries()].sort((a, b) => this.sortGeographyTypes(a[0], b[0]))
        );

        this.selectedItemTypesProvider.next(this.selectedItemTypes);
    }

    /**
     * Sorts geography types
     *
     * @param a - list element to sort
     * @param b - list element to sort
     *
     * @returns number
     */
    sortGeographyTypes(a: GeographyTypesEnum, b: GeographyTypesEnum): number {
        const aRanking: number = geographyTypeRankingMap.get(a);
        const bRanking: number = geographyTypeRankingMap.get(b);

        return aRanking - bRanking;
    }

    /**
     * Called when an item in the selectedItems list Is deleted from the
     * display poriton of the component (the x was clicked)
     *
     * @param item - item to remove from the region tree
     */
    displayItemDeleted(item: TreeNode, removeChildren: boolean = true): void {
        this.syncPartialSelectionOfParentNodesForDeletedItem(item);

        // removes the deleted regions from display list
        this.selectedListItemsToDisplay = this.selectedListItemsToDisplay.filter(listItem => {
            if (
                listItem.data.geographicType === item.data.geographicType
                && listItem.data.id === item.data.id
            ) {
                return false;
            }

            return true;
        });

        let childrenToRemove;
        if (item.children && removeChildren) {
            childrenToRemove = item.children.slice();
        }

        // removes all the children of the deleted region
        this.flattenedSelectedRegions = this.flattenedSelectedRegions.filter(selectedRegion => {
            if (
                selectedRegion.data.geographicType === item.data.geographicType
                && selectedRegion.data.id === item.data.id
            ) {
                return false;
            }

            if (childrenToRemove && childrenToRemove.length) {
                let selectedRegionIsChildOfItem = false;

                childrenToRemove.forEach(child => {
                    if (  selectedRegion.data.geographicType === child.data.geographicType
                        && selectedRegion.data.id === child.data.id
                    ) {
                        selectedRegionIsChildOfItem = true;
                    }
                });

                return !selectedRegionIsChildOfItem;
            }

            return true;
        });

        this.listItemProvider.next(this.listItems);
        this.setSelectedItemTypes();
        this.selectedItemsToDisplayProvider.next(this.selectedListItemsToDisplay);
        this.flattenedSelectedListItemsProvider.next(this.flattenedSelectedRegions);
    }

    /**
     * Syncs the flattened list of selected items, with the available list items, by adjusting the
     * partial selection status of available list items that are relatives of the the deleted item
     */
    syncPartialSelectionOfParentNodesForDeletedItem(item: TreeNode) {
        // an object whose keys will be the region levels of the deleted items parent nodes
        const relevantParents: Map<GeographyTypesEnum, PartiallySelectedParentsModel> = new Map();

        // assembles relevantParents
        this.regionTypes.forEach(regionType => {
            const parent = item.data[regionType.toLowerCase()];
            if (parent) {
                parent.geographicType = regionType;
                // keeps partial selection map up to date
                relevantParents.set(regionType, new PartiallySelectedParentsModel(false, parent));
            }
        });

        // find if any siblings/ cousins of the deleted item exist
        for (let i = 0; i < this.flattenedSelectedRegions.length; i++) {
            const node = this.flattenedSelectedRegions[i];

            if ((node.data as JournodexGeographyModel).isChildOf(item.data)) {
                // selected item is a direct child of the deleted item
                continue;
            }

            if (
                node.data.geographicType === item.data.geographicType
                && node.data.id === item.data.id
            ) {
                // this is the same node that is being deleted
                continue;
            }

            this.regionTypes.forEach(type => {
                const nodeParent = node.data[type.toLowerCase()];
                if (!nodeParent) {
                    return;
                }
                const mappedValueOfParentRegion = relevantParents.get(type);

                if (
                    mappedValueOfParentRegion
                    && mappedValueOfParentRegion.data.name === nodeParent.name
                    && mappedValueOfParentRegion.data.id === nodeParent.id
                ) {
                    // a relative exists, ensure this parent is partially selected
                    mappedValueOfParentRegion.partiallySelected = true;
                    relevantParents.set(type, mappedValueOfParentRegion);
                }
            });
        }

        this.walkItemsAndPartiallyUnselectChildren(relevantParents, this.listItems);
    }

    /**
     * Called when a region has been unselected in the component tree
     *
     * @param item
     */
    itemRemoved(item: TreeNode, removeChildren: boolean = true): void {
        this.removeSelectedChildRegionsOfItem(item, removeChildren);
        this.removeItemFromPartialSelectionMap(item.data);

        let parent = item.parent;
        while (parent) {
            this.removeItemFromPartialSelectionMap(parent.data);
            parent = parent.parent;
        }
    }

    removeSelectedChildRegionsOfItem(item: TreeNode, removeChildren: boolean): void {
        // for certain scenarios (ex: Target Audience Region)
        // we do not want to unselect the child nodes when the parent is unselected
        if (removeChildren) {
            const parentGeographicType = item.type.toLowerCase();
            const parentId = item.data.id;

            this.flattenedSelectedRegions = this.flattenedSelectedRegions.filter(region => {
                return region.data[parentGeographicType]?.id !== parentId;
            });
        }

        this.flattenedSelectedListItemsProvider.next(this.flattenedSelectedRegions);

        if (removeChildren) {
            this.selectedListItemsToDisplay = this.filterOutNodeChildren(this.flattenedSelectedRegions);
        } else {
            this.selectedListItemsToDisplay = this.flattenedSelectedRegions;
        }
        this.setSelectedItemTypes();
        this.selectedItemsToDisplayProvider.next(this.selectedListItemsToDisplay);
    }

    /**
     * Fetches child regions when tree node is expanded
     *
     * @param item node expanded
     * @param setItemValue we want to override the itemIsSelected to be false
     */
    nodeExpanded(item: TreeNode, setItemValue: boolean = true): void {
        if (
            item.children
            && item.children.length > 0
        ) {
            return;
        }

        let itemIsSelected = false;

        // for certain scenarios (ex: Target Audience Region)
        // we do not want to set itemIsSelected to true
        if (setItemValue) {
            this.flattenedSelectedRegions.forEach(
                selectedNode => {
                    if (
                        selectedNode.data.geographicType === item.data.geographicType
                        && selectedNode.data.id === item.data.id
                    ) {
                        itemIsSelected = true;
                    }
                }
            );
        }

        this.getImmediateChildrenOfItem(item, itemIsSelected);
    }

    /**
     * Collapses child nodes as well as node provided
     *
     * @param item node to collapse
     */
    nodeCollapsed(item: TreeNode): void {
        if (!item.children || !item.children.length) {
            return;
        }
        this.walkAndCollapseChildNodes(item.children);
    }

    /**
     * Collapses nodes for provided
     */
    walkAndCollapseChildNodes(children: TreeNode[]) {
        children.forEach(child => {
            child.expanded = false;
            if (child.children && child.children.length) {
                this.walkAndCollapseChildNodes(child.children);
            }
        });
    }

    /**
     * Sets state of service to either includedRegions or excludedRegions
     *
     * @param state either included/excluded regions
     */
    setState(state: IncludeOrExclude): void {
        this.state = state;

        this.partiallySelectedParentsMap = new Map(
            [
                [GeographyTypesEnum.continent, []],
                [GeographyTypesEnum.country, []],
                [GeographyTypesEnum.province, []],
                [GeographyTypesEnum.county, []]
            ]
        );
        this.setAppropriateListItems();
        this.getContinentRegions();
    }

    /**
     * Sets selectedItems & selectedItemsToDisplay in the component
     *
     * @param selectedRegions regions previously selected
     */
    setSelectedRegions(selectedRegions: JournodexGeographyModel[]): void {
        this.flattenedSelectedRegions = [];
        this.selectedListItemsToDisplay = [];
        selectedRegions.forEach(
            region => {
                this.setPartiallySelectedParents(region);
                const convertedRegion = this.getTreeNodeFromJournodexModel(region);

                this.flattenedSelectedRegions.push(convertedRegion);
                this.selectedListItemsToDisplay.push(convertedRegion);
            }
        );

        this.flattenedSelectedListItemsProvider.next(this.flattenedSelectedRegions);
        this.setSelectedItemTypes();
        this.selectedItemsToDisplayProvider.next(this.selectedListItemsToDisplay);
    }

    /**
     * Gets the regions at the continent level
     */
    getContinentRegions(): void {
        this.geographyService.getContinents()
            .subscribe(
                results => {

                    this.listItems = this.convertApiResponseToTreeNode(results).slice();
                    this.listItems.sort((a, b) => {
                        return a.label.localeCompare(b.label);
                    });

                    this.listItems.forEach(
                        (item, index) => {
                            const selectedItem = this.flattenedSelectedRegions.find(
                                selectedNode => {
                                    return (
                                        selectedNode.data.id === item.data.id
                                        && selectedNode.type === GeographyTypesEnum.continent
                                    );
                                }
                            );
                            if (selectedItem) {
                                this.listItems[index] = selectedItem;
                            }
                        }
                    );
                    this.listItemProvider.next(this.listItems);
                }
            );
    }

    /**
     * Fetches immediateChildren of item and applies them to the TreeNode model
     *
     * @param listItem - item to fetch children for
     * @param listItemSelected - true if listItem is a selected region
     */
    getImmediateChildrenOfItem(listItem: TreeNode, listItemSelected?: boolean): void {
        const parentJournodexModel = listItem.data as JournodexGeographyModel;

        this.geographyService.getChildRegions(parentJournodexModel)
            .subscribe(
                response => {
                    if (response && response.length) {
                        const newChildRegions =  this.convertApiResponseToTreeNode(response, listItem);

                        if (!newChildRegions.length) {
                            // if no children set item non expandable
                            listItem.children = null;
                            listItem.leaf = true;

                            return;
                        }

                        newChildRegions.sort( (a, b) => {
                            return a.label.localeCompare(b.label);
                        });

                        newChildRegions.forEach(
                            (child, index) => {
                                const indexOfExistingSelectedRegion = this.flattenedSelectedRegions.findIndex(
                                    node =>  {
                                        if (!node.data) {
                                            return false;
                                        }

                                        return node.data.id === child.data.id;
                                    }
                                );

                                if (indexOfExistingSelectedRegion !== -1) {
                                    this.flattenedSelectedRegions[indexOfExistingSelectedRegion] = child;
                                } else if (listItemSelected) {
                                    if (child.selectable) {
                                        child.partialSelected = false;
                                        this.flattenedSelectedRegions.push(child);
                                    }
                                }
                            }
                        );
                        listItem.children = newChildRegions;
                        this.listItems = this.cloneTree(this.listItems);
                        this.listItemProvider.next(this.listItems);
                        this.flattenedSelectedListItemsProvider.next(this.flattenedSelectedRegions);

                        if (listItemSelected) {
                            this.selectedListItemsToDisplay = this.filterOutNodeChildren(this.flattenedSelectedRegions);
                        } else {
                            this.selectedListItemsToDisplay = this.flattenedSelectedRegions;
                        }

                        this.setSelectedItemTypes();
                        this.selectedItemsToDisplayProvider.next(this.selectedListItemsToDisplay);
                    }
                }
            );
    }

    /**
     * Converts an array of QuickSearchModelsResponse models to tree node array
     *
     * @param childRegions models to be converted
     *
     * @returns TreeNode[]
     */
    convertApiResponseToTreeNode(childRegions: JournodexGeographyModel[], parentNode?: TreeNode): TreeNode[] {
        const treeNodes: TreeNode[] = [];
        for (let i = 0; i < childRegions.length; i++) {
            const region = childRegions[i];

            const tree = this.getTreeNodeFromJournodexModel(region, parentNode);
            treeNodes.push(tree);
        }

        return treeNodes;
    }

    /**
     * Converts a journodex model to tree node
     *
     * @param journodexModel data model to be converted
     * @param regionName label value for tree node
     * @param parentNode the parent to the resulting model
     *
     * @returns TreeNode
     */
    getTreeNodeFromJournodexModel(
        journodexModel: JournodexGeographyModel,
        parentNode?: TreeNode
    ): TreeNode {
        const property = journodexModel.geographicType.toLowerCase();
        const regionName = journodexModel[property].name;
        let tree;
        const isPartiallySelected = this.checkForPartialSelection(journodexModel);

        // The node type here has 2 uses
        // 1. To template locked items in the tree view
        // 2. To categorize the selected items in the right panel
        // Because we are assuming that locked items can't end up on the right side, it will
        // work to share the field.
        if (!parentNode) {
            tree = <TreeNode> {
                label: regionName,
                data: journodexModel,
                type: journodexModel.isLocked ? 'locked' : journodexModel.geographicType,
                styleClass: journodexModel.isLocked ? 'agpr-checkbox-locked' : null,
                partialSelected: isPartiallySelected,
                leaf: journodexModel.isLocked,
                selectable: !journodexModel.isLocked,
                key: uuidv4()
            };
        } else if (journodexModel.geographicType === GeographyTypesEnum.city) {
            tree = <TreeNode> {
                label: regionName,
                data: journodexModel,
                parent: parentNode,
                partialSelected: false,
                type: journodexModel.isLocked ? 'locked' : journodexModel.geographicType,
                styleClass: journodexModel.isLocked ? 'agpr-checkbox-locked' : null,
                leaf: true,
                selectable: !journodexModel.isLocked,
                key: uuidv4()
            };
        } else {
            tree = <TreeNode> {
                label: regionName,
                data: journodexModel,
                parent: parentNode,
                partialSelected: isPartiallySelected,
                type: journodexModel.isLocked ? 'locked' : journodexModel.geographicType,
                styleClass: journodexModel.isLocked ? 'agpr-checkbox-locked' : null,
                leaf: journodexModel.isLocked,
                children: [],
                selectable: !journodexModel.isLocked,
                key: uuidv4()
            };
        }

        return tree;
    }

    /**
     * Maintains the partiallySelectedParent mapping by removing a region from the mapping
     *
     * @param item - region to remove from the paritally selected parent mapping
     */
    removeItemFromPartialSelectionMap(item: JournodexGeographyModel): void {
        const geographyType = item.geographicType;
        let continents = this.partiallySelectedParentsMap.get(GeographyTypesEnum.continent);
        let countries = this.partiallySelectedParentsMap.get(GeographyTypesEnum.country);
        let provinces = this.partiallySelectedParentsMap.get(GeographyTypesEnum.province);
        let counties = this.partiallySelectedParentsMap.get(GeographyTypesEnum.county);

        switch (geographyType) {
            case GeographyTypesEnum.county:
                counties = counties.filter(county => {
                    return county.id !== item.id;
                });
                this.partiallySelectedParentsMap.set(GeographyTypesEnum.county, counties);
                break;
            case GeographyTypesEnum.province:
                provinces = provinces.filter(province => {
                    return province.id !== item.id;
                });
                this.partiallySelectedParentsMap.set(GeographyTypesEnum.province, provinces);
                break;
            case GeographyTypesEnum.country:
                countries = countries.filter(country => {
                    return country.id !== item.id;
                });
                this.partiallySelectedParentsMap.set(GeographyTypesEnum.country, countries);
                break;
            case GeographyTypesEnum.continent:
                continents = continents.filter(continent => {
                    return continent.id !== item.id;
                });
                this.partiallySelectedParentsMap.set(GeographyTypesEnum.continent, continents);
                break;
        }
    }

    /**
     * Checks if a region item is in the partially selected mapping
     *
     *  @param item - region to check if its in the partially selected region mapping
     *
     * @returns boolean
     */
    checkForPartialSelection(item: JournodexGeographyModel): boolean {
        const geographyType = item.geographicType;
        const continents = this.partiallySelectedParentsMap.get(GeographyTypesEnum.continent);
        const countries = this.partiallySelectedParentsMap.get(GeographyTypesEnum.country);
        const provinces = this.partiallySelectedParentsMap.get(GeographyTypesEnum.province);
        const counties = this.partiallySelectedParentsMap.get(GeographyTypesEnum.county);

        switch (geographyType) {
            case GeographyTypesEnum.county:
                const county = counties.find(geographicModel => {
                    return geographicModel.id === item.county.id;
                });

                if (county) {
                    return true;
                }
                break;
            case GeographyTypesEnum.province:
                const province = provinces.find(geographicModel => {
                    return geographicModel.id === item.province.id;
                });

                if (province) {
                    return true;
                }
                break;
            case GeographyTypesEnum.country:
                const country = countries.find(geographicModel => {
                    return geographicModel.id === item.country.id;
                });

                if (country) {
                    return true;
                }
                break;
            case GeographyTypesEnum.continent:
                const continent = continents.find(geographicModel => {
                    return geographicModel.id === item.continent.id;
                });

                if (continent) {
                    return true;
                }
                break;
        }

        return false;
    }

    /**
     *  Sets a region as partially selected in the mapping
     *
     * @param item
     */
    setPartiallySelectedParents(region: JournodexGeographyModel) {
        const geographyType = region.geographicType;
        const continents = this.partiallySelectedParentsMap.get(GeographyTypesEnum.continent);
        const countries = this.partiallySelectedParentsMap.get(GeographyTypesEnum.country);
        const provinces = this.partiallySelectedParentsMap.get(GeographyTypesEnum.province);
        const counties = this.partiallySelectedParentsMap.get(GeographyTypesEnum.county);

        switch (geographyType) {
            case GeographyTypesEnum.city:
                if (region.county) {
                    counties.push(region.county);
                    this.partiallySelectedParentsMap.set(GeographyTypesEnum.county, counties);
                }
                /* falls through*/
            case GeographyTypesEnum.county:
                if (region.province) {
                    provinces.push(region.province);
                    this.partiallySelectedParentsMap.set(GeographyTypesEnum.province, provinces);
                }
                /* falls through*/
            case GeographyTypesEnum.province:
                if (region.country) {
                    countries.push(region.country);
                    this.partiallySelectedParentsMap.set(GeographyTypesEnum.country, countries);
                }
                /* falls through*/
            case GeographyTypesEnum.country:
                if (region.continent) {
                    continents.push(region.continent);
                    this.partiallySelectedParentsMap.set(GeographyTypesEnum.continent, continents);
                }
                break;
        }
    }

    /**
     * Handles change in selected list items
     * @param selectedItems most recent list of selected nodes
     */
    selectionChange(selectedItems: TreeNode[], listItems: TreeNode[], filterChildren: boolean = true) {
        selectedItems = selectedItems.filter(x => x.selectable);
        this.flattenedSelectedRegions = selectedItems;

        this.selectedListItemsToDisplay = this.flattenedSelectedRegions.slice();

        // By default the tree selects all children when you select a thing,
        // even if they are selectable=false.  So we have to next() the data again
        // so the tree is accurate.
        this.flattenedSelectedListItemsProvider.next(this.flattenedSelectedRegions);

        // for certain scenarios (ex: Target Audience Region)
        // we always want the parent & the child to be selected
        // this function makes it so only the parent is selected
        if (filterChildren) {
            this.filterSelectedChildRegionsThatDontKnowThierOwnParents();
        }
        this.setSelectedItemTypes();

        this.selectedItemsToDisplayProvider.next(this.selectedListItemsToDisplay);
    }

    /**
     * Filters out region TreeNodes that do not have a parents property on their TreeNode model,
     * but their parent is currently a selected region
     *
     * Necessary for selected regions imported from the searchQuery, the conversion to
     * TreeNode from journodexGeographyModel will not find their parent regions if they
     * are not part of the available regions.  As nodes are expanded and more regions
     * are added to the listItems parent nodes may be added.
     *
     * If these are later selected we need to remove child treeNodes that do not
     * list the selected parent as a parent element.
     */
    filterSelectedChildRegionsThatDontKnowThierOwnParents(): void {
        const idCache = {};
        const newItems = [];
        const removedItems = [];

        this.selectedListItemsToDisplay.forEach( (selectedRegion) => {
            const thing = selectedRegion.data as JournodexGeographyModel;
            idCache[`${thing.geographicType}+${thing.id}`] = thing;
        });
        this.selectedListItemsToDisplay.forEach( (selectedRegion) => {
            if (
                selectedRegion.data.geographicType !== GeographyTypesEnum.continent
                && selectedRegion.data.continent
                && idCache[`${GeographyTypesEnum.continent}+${selectedRegion.data.continent.id}`]
            ) {
                removedItems.push(selectedRegion);
            } else if (
                selectedRegion.data.geographicType !== GeographyTypesEnum.country
                && selectedRegion.data.country
                && idCache[`${GeographyTypesEnum.country}+${selectedRegion.data.country.id}`]
            ) {
                removedItems.push(selectedRegion);
            } else if (
                selectedRegion.data.geographicType !== GeographyTypesEnum.province
                && selectedRegion.data.province
                && idCache[`${GeographyTypesEnum.province}+${selectedRegion.data.province.id}`]
            ) {
                removedItems.push(selectedRegion);
            } else if (
                selectedRegion.data.geographicType !== GeographyTypesEnum.county
                && selectedRegion.data.county
                && idCache[`${GeographyTypesEnum.county}+${selectedRegion.data.county.id}`]
            ) {
                removedItems.push(selectedRegion);
            } else {
                newItems.push(selectedRegion);
            }
        });
        this.selectedListItemsToDisplay = newItems;

        removedItems.forEach((oldRegion) => {
            if (oldRegion.parent) {
                return;
            }
            const index = this.flattenedSelectedRegions.indexOf(oldRegion);
            this.flattenedSelectedRegions.splice(index, 1);
        });
        this.flattenedSelectedListItemsProvider.next(this.flattenedSelectedRegions);
    }

    /**
     * Filters out child elements of a list and returns of list contianing only the
     * highest level of a tree of related nodes
     *
     * @param nodes list of elements
     *
     * @returns TreeNode[] - a list of only the highest level TreeNodes
     */
    filterOutNodeChildren(nodes: TreeNode[]): TreeNode[] {
        nodes.forEach( node => {
            if (!node.children) {
                return;
            }

            node.children.forEach( childNode => {
                nodes = nodes.filter( nodeItem => {
                    return childNode.label !== nodeItem.label;
                });
            });
        });

        return nodes;
    }

    getSelectedItems(): JournodexGeographyModel[] {
        return this.selectedRegions;
    }

    reset(): void {
        this.unselectNodes(this.listItems);
        this.listItemProvider.next(this.listItems);
        this.flattenedSelectedRegions = [];
        this.flattenedSelectedListItemsProvider.next(this.flattenedSelectedRegions);
        this.selectedListItemsToDisplay = [];
        this.selectedItemsToDisplayProvider.next(this.selectedListItemsToDisplay);
        this.selectedItemTypes = new Map<GeographyTypesEnum, string>();
        this.selectedItemTypesProvider.next(this.selectedItemTypes);
        this.partiallySelectedParentsMap = new Map(
            [
                [GeographyTypesEnum.continent, []],
                [GeographyTypesEnum.country, []] ,
                [GeographyTypesEnum.province, []],
                [GeographyTypesEnum.county, []],
            ]
        );
    }

    unselectNodes(nodes: TreeNode<any>[]){
        nodes.forEach(node => {
            node.partialSelected = false;
            if (node.children){
                this.unselectNodes(node.children);
            }
        });
    }
}
